import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import OneSignal from 'onesignal-cordova-plugin';
import { telemetry } from '../../../services/backend';
import { captureEvent } from '../../../services/event-capture';

// Matches BackgroundGeolocation authorization constants (0–4).
const BACKGROUND_LOCATION_STATUS: Record<number, string> = {
  0: 'notDetermined',
  1: 'restricted',
  2: 'denied',
  3: 'always',
  4: 'whenInUse',
};

type DiagnosticPlugin = {
  getPermissionAuthorizationStatus: (
    success: (status: string) => void,
    error: (error: unknown) => void,
    permission: string,
  ) => void;
  getMotionAuthorizationStatus: (
    success: (status: string) => void,
    error: (error: unknown) => void,
  ) => void;
  permission?: { ACTIVITY_RECOGNITION?: string };
};

let hasLoggedPermissionSnapshot = false;

export const PERMISSION_READ_TIMEOUT_MS = 5000;

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const getPlatformSafe = (): string => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'unknown';
  }
};

async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('timed out'));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

const readOrError = async (read: () => Promise<unknown>): Promise<unknown> => {
  try {
    return await withTimeout(read(), PERMISSION_READ_TIMEOUT_MS);
  } catch (error) {
    return `error: ${error instanceof Error ? error.message : String(error)}`;
  }
};

const readDiagnosticStatus = async (): Promise<unknown> => {
  const diagnostic = (
    window as Window & {
      cordova?: { plugins?: { diagnostic?: DiagnosticPlugin } };
    }
  ).cordova?.plugins?.diagnostic;

  if (!diagnostic) {
    return 'unavailable';
  }

  const platform = Capacitor.getPlatform();

  const activityRecognition = diagnostic.permission?.ACTIVITY_RECOGNITION;
  if (platform === 'android' && activityRecognition) {
    return new Promise((resolve) => {
      diagnostic.getPermissionAuthorizationStatus(
        (status) => resolve(status),
        (error) =>
          resolve(
            `error: ${error instanceof Error ? error.message : String(error)}`,
          ),
        activityRecognition,
      );
    });
  }

  if (platform === 'ios' && diagnostic.getMotionAuthorizationStatus) {
    return new Promise((resolve) => {
      diagnostic.getMotionAuthorizationStatus(
        (status) => resolve(status),
        (error) =>
          resolve(
            `error: ${error instanceof Error ? error.message : String(error)}`,
          ),
      );
    });
  }

  return 'unavailable';
};

export const collectPermissionSnapshot = async (): Promise<
  Record<string, unknown>
> => {
  const [notificationGranted, notificationOptedIn, location, camera, motion] =
    await Promise.all([
      readOrError(() => OneSignal.Notifications.getPermissionAsync()),
      readOrError(() => OneSignal.User.pushSubscription.getOptedInAsync()),
      readOrError(() => Geolocation.checkPermissions()),
      readOrError(() => Camera.checkPermissions()),
      readOrError(() => readDiagnosticStatus()),
    ]);

  const backgroundLocation = await readOrError(async () => {
    const { status } = await BackgroundGeolocation.getProviderState();
    return BACKGROUND_LOCATION_STATUS[status] ?? `unknown:${status}`;
  });

  const readNamedStatus = (value: unknown, key: string): unknown => {
    if (value && typeof value === 'object' && key in value) {
      return (value as Record<string, unknown>)[key];
    }
    return undefined;
  };

  return {
    platform: Capacitor.getPlatform(),
    notificationGranted,
    notificationOptedIn,
    locationForeground: readNamedStatus(location, 'location') ?? location,
    locationCoarse: readNamedStatus(location, 'coarseLocation'),
    locationBackground: backgroundLocation,
    motion,
    camera: readNamedStatus(camera, 'camera') ?? camera,
    photos: readNamedStatus(camera, 'photos'),
  };
};

/**
 * Read-only dump of OS permission state. Must never call request* APIs.
 * Once per app load on native; no-op on web.
 */
export const logPermissionSnapshot = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform() || hasLoggedPermissionSnapshot) {
    return;
  }
  hasLoggedPermissionSnapshot = true;

  try {
    const payload = await collectPermissionSnapshot();
    telemetry(
      telemetry.INFO,
      `[logPermissionSnapshot] ${JSON.stringify(payload)}`,
    );
    captureEvent({
      type: 'app: permission snapshot',
      payload,
    });
  } catch (error) {
    hasLoggedPermissionSnapshot = false;
    const payload = {
      reason: errorMessage(error),
      platform: getPlatformSafe(),
    };
    // eslint-disable-next-line no-console -- HyperDX browser console capture
    console.error('app: permission snapshot failed', payload);
    try {
      captureEvent({
        type: 'app: permission snapshot failed',
        payload,
      });
    } catch {
      // console.error already emitted for HyperDX console capture
    }
  }
};

export const logNotificationPermissionResult = ({
  alreadyGranted,
  requested,
  granted,
}: {
  alreadyGranted: boolean;
  requested: boolean;
  granted: boolean;
}): void => {
  const payload = {
    alreadyGranted,
    requested,
    granted,
    decision: granted ? 'approved' : 'denied',
  };

  telemetry(
    telemetry.INFO,
    `[logNotificationPermissionResult] ${JSON.stringify(payload)}`,
  );
  captureEvent({
    type: 'app: notification permission result',
    payload,
  });
};

export const resetPermissionSnapshotLogForTests = (): void => {
  hasLoggedPermissionSnapshot = false;
};
